import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Plus, Edit2, Wrench, AlertTriangle, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MaintenancePage = () => {
  const { t } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [formData, setFormData] = useState({
    unit_id: '',
    category: '',
    description: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, unitsRes] = await Promise.all([
        axios.get(`${API_URL}/api/maintenance-requests`, { withCredentials: true }),
        axios.get(`${API_URL}/api/units`, { withCredentials: true })
      ]);
      setRequests(requestsRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRequest) {
        await axios.put(`${API_URL}/api/maintenance-requests/${editingRequest.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/maintenance-requests`, formData, { withCredentials: true });
      }
      toast.success(t('saveSuccess'));
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/maintenance-requests/${id}`, { status: newStatus }, { withCredentials: true });
      toast.success(t('saveSuccess'));
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleEdit = (request) => {
    setEditingRequest(request);
    setFormData({
      unit_id: request.unit_id || '',
      category: request.category || '',
      description: request.description || '',
      priority: request.priority || 'medium',
      status: request.status || 'open',
      assigned_staff: request.assigned_staff || '',
      notes: request.notes || ''
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingRequest(null);
    setFormData({
      unit_id: '',
      category: '',
      description: '',
      priority: 'medium'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-red-50 text-red-700 border-red-600',
      in_progress: 'bg-amber-50 text-amber-700 border-amber-600',
      resolved: 'bg-emerald-50 text-emerald-700 border-emerald-600'
    };
    return `px-2 py-1 text-xs font-bold uppercase border ${styles[status] || 'bg-gray-100'}`;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-50 text-gray-700',
      medium: 'bg-blue-50 text-blue-700',
      high: 'bg-orange-50 text-orange-700',
      urgent: 'bg-red-50 text-red-700'
    };
    return `px-2 py-1 text-xs font-bold uppercase ${styles[priority] || 'bg-gray-100'}`;
  };

  const getUnitName = (unitId) => units.find(u => u.id === unitId)?.name || '-';

  const filteredRequests = requests.filter(r => 
    statusFilter === 'all' || r.status === statusFilter
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('maintenance')}</h1>
          <p className="text-gray-500 text-sm mt-1">{requests.length} {t('total')}</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-black text-white rounded-none hover:bg-gray-800"
          data-testid="add-maintenance-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('addRequest')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-red-200 p-4 flex items-center gap-4">
          <div className="p-3 bg-red-100 rounded-full">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{requests.filter(r => r.status === 'open').length}</p>
            <p className="text-xs uppercase text-gray-500">{t('open')}</p>
          </div>
        </div>
        <div className="bg-white border border-amber-200 p-4 flex items-center gap-4">
          <div className="p-3 bg-amber-100 rounded-full">
            <Wrench className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{requests.filter(r => r.status === 'in_progress').length}</p>
            <p className="text-xs uppercase text-gray-500">{t('inProgress')}</p>
          </div>
        </div>
        <div className="bg-white border border-emerald-200 p-4 flex items-center gap-4">
          <div className="p-3 bg-emerald-100 rounded-full">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{requests.filter(r => r.status === 'resolved').length}</p>
            <p className="text-xs uppercase text-gray-500">{t('resolved')}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 rounded-none" data-testid="maintenance-status-filter">
            <SelectValue placeholder={t('status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="open">{t('open')}</SelectItem>
            <SelectItem value="in_progress">{t('inProgress')}</SelectItem>
            <SelectItem value="resolved">{t('resolved')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Requests Table */}
      <div className="border border-gray-200 overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr className="bg-gray-50">
              <th>{t('units')}</th>
              <th>{t('category')}</th>
              <th>{t('description')}</th>
              <th>{t('priority')}</th>
              <th>{t('status')}</th>
              <th>{t('assignedStaff')}</th>
              <th>{t('createdAt')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.length > 0 ? (
              filteredRequests.map((request) => (
                <tr key={request.id} data-testid={`maintenance-row-${request.id}`}>
                  <td className="font-medium">{getUnitName(request.unit_id)}</td>
                  <td>{t(request.category) || request.category}</td>
                  <td className="max-w-xs truncate">{request.description}</td>
                  <td>
                    <span className={getPriorityBadge(request.priority)}>
                      {t(request.priority) || request.priority}
                    </span>
                  </td>
                  <td>
                    <span className={getStatusBadge(request.status)}>
                      {request.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{request.assigned_staff || '-'}</td>
                  <td>{request.created_at?.split('T')[0]}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      {request.status === 'open' && (
                        <button 
                          onClick={() => handleStatusChange(request.id, 'in_progress')} 
                          className="p-1 hover:bg-amber-100 text-amber-600"
                          title="Start"
                        >
                          <Wrench className="w-4 h-4" />
                        </button>
                      )}
                      {request.status === 'in_progress' && (
                        <button 
                          onClick={() => handleStatusChange(request.id, 'resolved')} 
                          className="p-1 hover:bg-emerald-100 text-emerald-600"
                          title="Resolve"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleEdit(request)} 
                        className="p-1 hover:bg-gray-100"
                        data-testid={`edit-maintenance-${request.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-8 text-gray-500">
                  {t('noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editingRequest ? 'Edit Request' : t('addRequest')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('units')} *</label>
              <Select 
                value={formData.unit_id} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, unit_id: val }))}
                disabled={!!editingRequest}
              >
                <SelectTrigger className="rounded-none mt-1" data-testid="maintenance-unit-select">
                  <SelectValue placeholder={t('selectUnit')} />
                </SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('category')} *</label>
                <Select 
                  value={formData.category} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, category: val }))}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue placeholder={t('selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plumbing">{t('plumbing')}</SelectItem>
                    <SelectItem value="electrical">{t('electrical')}</SelectItem>
                    <SelectItem value="hvac">{t('hvac')}</SelectItem>
                    <SelectItem value="appliance">{t('appliance')}</SelectItem>
                    <SelectItem value="structural">{t('structural')}</SelectItem>
                    <SelectItem value="other">{t('other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('priority')}</label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t('low')}</SelectItem>
                    <SelectItem value="medium">{t('medium')}</SelectItem>
                    <SelectItem value="high">{t('high')}</SelectItem>
                    <SelectItem value="urgent">{t('urgent')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('description')} *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                required
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-black"
                rows="4"
              />
            </div>

            {editingRequest && (
              <>
                <div>
                  <label className="text-sm font-medium">{t('assignedStaff')}</label>
                  <Input
                    value={formData.assigned_staff || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, assigned_staff: e.target.value }))}
                    className="rounded-none mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('notes')}</label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-black"
                    rows="3"
                  />
                </div>
              </>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800" data-testid="maintenance-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenancePage;
